import { ref } from 'vue'
import { defineStore } from 'pinia'
import { getSettings, getLunch, getRoles, getEmployeeStatisticsSettings } from '@/api/settings'
import type { EmployeeStatisticsSettings, Role } from '@/types'
import type { GlobalSettings, LunchRule } from '@/components/settings/settingsModel'

// Подтверждённые сервером данные раздела «Настройки». Черновики RD-163 живут в редакторе раздела
// (src/components/settings/useSettingsEditor.ts) и попадают сюда только после успешной записи.

const isObject = (value: unknown) => Boolean(value) && typeof value === 'object' && !Array.isArray(value)

/** Тело ответа без данных (в т.ч. `status: 'error'`) — ошибка загрузки, не пустые данные. */
function readBody<T>(data: any, check: (body: unknown) => boolean): T {
  if (!data || data.status === 'error' || !check(data.body)) {
    throw new Error(data?.msg || 'Некорректный ответ сервера')
  }
  return data.body as T
}

export const useSettingsStore = defineStore('settings', () => {
  // Состояния
  const loading = ref(false)
  const settings = ref<GlobalSettings | null>(null)
  const employeeStatisticsSettings = ref<EmployeeStatisticsSettings | null>(null)
  const employeeStatisticsSettingsLoading = ref(false)
  const lunchSettings = ref<LunchRule[]>([])
  const roles = ref<Role[]>([])

  // Функции загрузки: при ошибке возвращают её (прежний контракт), данные не подменяются.
  async function fetchSettings() {
    loading.value = true
    try {
      const res = await getSettings()
      settings.value = readBody<GlobalSettings>(res.data, isObject)
    } catch (err: any) {
      return err
    } finally {
      loading.value = false
    }
  }

  async function fetchEmployeeStatisticsSettings() {
    employeeStatisticsSettingsLoading.value = true
    try {
      const res = await getEmployeeStatisticsSettings()
      employeeStatisticsSettings.value = readBody<EmployeeStatisticsSettings>(res.data, isObject)
    } catch (err: any) {
      return err
    } finally {
      employeeStatisticsSettingsLoading.value = false
    }
  }

  async function fetchLunchSettings() {
    loading.value = true
    try {
      const res = await getLunch()
      lunchSettings.value = readBody<LunchRule[]>(res.data, Array.isArray)
      sortLunches()
    } catch (err: any) {
      return err
    } finally {
      loading.value = false
    }
  }

  function sortLunches() {
    lunchSettings.value.sort((a, b) => a.shift_duration_seconds - b.shift_duration_seconds)
  }

  async function fetchRoles() {
    try {
      const res = await getRoles()
      roles.value = readBody<Role[]>(res.data, Array.isArray)
    } catch (err: any) {
      return err
    }
  }

  return {
    // Состояния
    loading,
    settings,
    employeeStatisticsSettings,
    employeeStatisticsSettingsLoading,
    lunchSettings,
    roles,
    // Функции
    fetchSettings,
    fetchEmployeeStatisticsSettings,
    fetchLunchSettings,
    fetchRoles,
  }
})
